/**
 * Calendar step — two-month date range picker + guest selection.
 *
 * Pure vanilla TS. Renders into a host element and emits state via
 * an `onChange` callback. Re-renders on internal state changes via a
 * minimal local diffing strategy (innerHTML for the calendar grid,
 * delegated event listeners).
 */

import type {
  AvailabilityResponse,
  PriceBreakdown,
  BookingAPI,
  BlockedRange,
} from '../api.js';
import { APIError } from '../api.js';
import {
  addDays,
  addMonths,
  buildMonthMatrix,
  diffDays,
  formatMonthLabel,
  fromISODate,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isWithin,
  rangeIntersectsBlocked,
  startOfDay,
  toISODate,
  today,
  formatHumanDate,
} from '../utils/dates.js';
import {
  estimateBreakdown,
  formatPence,
} from '../pricing.js';
import { escapeHTML, pluralise, cleanText } from '../utils/formatting.js';

export interface CalendarState {
  checkIn: Date | null;
  checkOut: Date | null;
  numAdults: number;
  numChildren: number;
  childAges: string[];
  hasPets: boolean;
  /** Last server-confirmed price (overrides local estimate when present). */
  serverPrice: PriceBreakdown | null;
}

export interface CalendarStepOptions {
  api: BookingAPI;
  flow: 'booking' | 'enquiry';
  initial?: Partial<CalendarState>;
  onValidityChange: (valid: boolean) => void;
  onStateChange: (state: CalendarState) => void;
}

const MAX_ADULTS_FALLBACK = 8;
const MAX_OCC_FALLBACK = 10;

export class CalendarStep {
  private readonly host: HTMLElement;
  private readonly opts: CalendarStepOptions;

  private state: CalendarState;
  private availability: AvailabilityResponse | null = null;
  private availabilityError: string | null = null;
  private leftMonth: Date;

  private priceLoadingToken = 0;
  private priceLoading = false;
  private priceError: string | null = null;
  private destroyed = false;
  private hoverDate: Date | null = null;
  private availabilityLoadingToken = 0;

  constructor(host: HTMLElement, opts: CalendarStepOptions) {
    this.host = host;
    this.opts = opts;
    this.state = {
      checkIn: opts.initial?.checkIn ?? null,
      checkOut: opts.initial?.checkOut ?? null,
      numAdults: opts.initial?.numAdults ?? 2,
      numChildren: opts.initial?.numChildren ?? 0,
      childAges: opts.initial?.childAges ?? [],
      hasPets: opts.initial?.hasPets ?? false,
      serverPrice: opts.initial?.serverPrice ?? null,
    };
    this.leftMonth = startOfDay(
      this.state.checkIn
        ? new Date(this.state.checkIn.getFullYear(), this.state.checkIn.getMonth(), 1)
        : today(),
    );

    this.render();
    void this.loadAvailability();
  }

  destroy(): void {
    this.destroyed = true;
    this.host.innerHTML = '';
  }

  getState(): CalendarState {
    return { ...this.state, childAges: [...this.state.childAges] };
  }

  /* ----------------------------------------------------------------- */
  /*  Data loading                                                     */
  /* ----------------------------------------------------------------- */

  private async loadAvailability(): Promise<void> {
    const token = ++this.availabilityLoadingToken;
    try {
      const result = await this.opts.api.getAvailability();
      if (this.destroyed) return;
      if (token !== this.availabilityLoadingToken) return;
      this.availability = result;
      this.availabilityError = null;
    } catch (e) {
      if (this.destroyed) return;
      if (token !== this.availabilityLoadingToken) return;
      this.availabilityError =
        e instanceof APIError
          ? e.message
          : 'Could not load availability. Please try again.';
    }
    if (token !== this.availabilityLoadingToken) return;
    this.render();
    this.emitValidity();
    this.maybeFetchPrice();
  }

  private async maybeFetchPrice(): Promise<void> {
    if (this.opts.flow !== 'booking') return;
    if (!this.state.checkIn || !this.state.checkOut) {
      this.state.serverPrice = null;
      this.priceError = null;
      this.priceLoading = false;
      this.renderPriceBox();
      return;
    }
    if (!this.meetsMinNights()) {
      this.state.serverPrice = null;
      this.priceError = null;
      this.priceLoading = false;
      this.renderPriceBox();
      return;
    }

    const token = ++this.priceLoadingToken;
    this.priceLoading = true;
    this.priceError = null;
    this.renderPriceBox();

    try {
      const price = await this.opts.api.calculatePrice({
        check_in: toISODate(this.state.checkIn),
        check_out: toISODate(this.state.checkOut),
        num_adults: this.state.numAdults,
        num_children: this.state.numChildren,
      });
      if (this.destroyed || token !== this.priceLoadingToken) return;
      this.state.serverPrice = price;
      this.priceLoading = false;
      this.opts.onStateChange(this.getState());
      this.renderPriceBox();
    } catch (e) {
      if (this.destroyed || token !== this.priceLoadingToken) return;
      this.priceLoading = false;
      this.priceError =
        e instanceof APIError
          ? e.message
          : 'Could not load price. Please try again.';
      this.state.serverPrice = null;
      this.renderPriceBox();
    }
  }

  /* ----------------------------------------------------------------- */
  /*  Validation                                                        */
  /* ----------------------------------------------------------------- */

  private requiredMinNights(): number {
    if (!this.availability) return 3;
    if (!this.state.checkIn) return this.availability.minNightsStandard;
    return this.isSummerStay(this.state.checkIn)
      ? this.availability.minNightsSummer
      : this.availability.minNightsStandard;
  }

  private isSummerStay(date: Date): boolean {
    if (!this.availability) return false;
    // Use month/day so the summer window is annual.
    const s = fromISODate(this.availability.summerStart);
    const e = fromISODate(this.availability.summerEnd);
    if (!s || !e) return false;
    const m = date.getMonth();
    const d = date.getDate();
    const sm = s.getMonth();
    const sd = s.getDate();
    const em = e.getMonth();
    const ed = e.getDate();
    const after = m > sm || (m === sm && d >= sd);
    const before = m < em || (m === em && d <= ed);
    if (sm <= em) return after && before;
    // window wraps year-end
    return after || before;
  }

  private meetsMinNights(): boolean {
    if (!this.state.checkIn || !this.state.checkOut) return false;
    const nights = diffDays(this.state.checkIn, this.state.checkOut);
    return nights >= this.requiredMinNights();
  }

  private allDaysAvailableInRange(): boolean {
    if (!this.state.checkIn || !this.state.checkOut) return false;
    if (!this.availability) return true;
    for (const r of this.availability.blockedRanges) {
      const bStart = fromISODate(r.start);
      const bEnd = fromISODate(r.end);
      if (!bStart || !bEnd) continue;
      if (
        rangeIntersectsBlocked(
          this.state.checkIn,
          this.state.checkOut,
          bStart,
          bEnd,
        )
      ) {
        return false;
      }
    }
    return true;
  }

  isValid(): boolean {
    if (!this.availability) return false;
    if (!this.state.checkIn || !this.state.checkOut) return false;
    if (!this.meetsMinNights()) return false;
    if (!this.allDaysAvailableInRange()) return false;
    if (this.state.numAdults < 1) return false;
    const maxAdults = this.availability.maxAdults || MAX_ADULTS_FALLBACK;
    const maxOcc = this.availability.maxOccupancy || MAX_OCC_FALLBACK;
    if (this.state.numAdults > maxAdults) return false;
    if (this.state.numAdults + this.state.numChildren > maxOcc) return false;
    if (this.state.numChildren > 0) {
      // every child must have a non-empty age label
      for (let i = 0; i < this.state.numChildren; i++) {
        const label = cleanText(this.state.childAges[i] ?? '');
        if (label.length === 0) return false;
      }
    }
    if (this.opts.flow === 'booking') {
      // Prefer server-confirmed price; fall back to localEstimate if the
      // server call briefly failed (cold-start JWT-race on calculate-price
      // is transient). create-booking re-computes authoritative pricing
      // server-side, so the guest is never charged based on the local
      // estimate — this only gates the Next button, not the actual charge.
      if (!this.state.serverPrice && !this.localEstimate()) return false;
    }
    return true;
  }

  private emitValidity(): void {
    this.opts.onValidityChange(this.isValid());
  }

  /* ----------------------------------------------------------------- */
  /*  Rendering                                                         */
  /* ----------------------------------------------------------------- */

  private render(): void {
    if (this.availabilityError && !this.availability) {
      this.host.innerHTML = `
        <div class="no4w-banner no4w-banner--error">
          ${escapeHTML(this.availabilityError)}
        </div>
        <button class="no4w-btn no4w-btn--secondary" data-action="retry-availability">Retry</button>
      `;
      const retry = this.host.querySelector<HTMLButtonElement>(
        '[data-action="retry-availability"]',
      );
      retry?.addEventListener('click', () => {
        this.availabilityError = null;
        this.render();
        void this.loadAvailability();
      });
      return;
    }

    if (!this.availability) {
      this.host.innerHTML = `
        <p class="no4w-hint" style="text-align:center;padding:24px 0;">
          <span class="no4w-loader no4w-loader--dark"></span> Loading availability…
        </p>
      `;
      return;
    }

    const maxAdults = this.availability.maxAdults || MAX_ADULTS_FALLBACK;
    const maxOcc = this.availability.maxOccupancy || MAX_OCC_FALLBACK;

    this.host.innerHTML = `
      <div class="no4w-cal-legend" aria-hidden="true">
        <span><i class="no4w-legend-selected"></i> Selected</span>
        <span><i class="no4w-legend-blocked"></i> Unavailable</span>
      </div>
      <div class="no4w-selected-range" data-role="selected-range">
        ${this.renderSelectedRange()}
      </div>
      <div class="no4w-cal" data-role="cal-grid"></div>

      <div class="no4w-guest-grid">
        <div class="no4w-guest-cell">
          <div class="no4w-guest-cell-label">
            <strong>Adults</strong>
            <span>Age 16+</span>
          </div>
          <div class="no4w-stepper" data-stepper="adults">
            <button type="button" data-step="-1" aria-label="Decrease adults">−</button>
            <span class="no4w-stepper-value" data-value>${this.state.numAdults}</span>
            <button type="button" data-step="+1" aria-label="Increase adults">+</button>
          </div>
        </div>
        <div class="no4w-guest-cell">
          <div class="no4w-guest-cell-label">
            <strong>Children</strong>
            <span>Age 0–15</span>
          </div>
          <div class="no4w-stepper" data-stepper="children">
            <button type="button" data-step="-1" aria-label="Decrease children">−</button>
            <span class="no4w-stepper-value" data-value>${this.state.numChildren}</span>
            <button type="button" data-step="+1" aria-label="Increase children">+</button>
          </div>
        </div>
      </div>

      <div class="no4w-child-ages" data-role="child-ages"></div>

      <div class="no4w-field" style="margin-top:14px;">
        <label>Travelling with pets?</label>
        <div class="no4w-toggle" data-role="pets">
          <button type="button" data-pets="no" class="${
            !this.state.hasPets ? 'is-active' : ''
          }">No</button>
          <button type="button" data-pets="yes" class="${
            this.state.hasPets ? 'is-active' : ''
          }">Yes</button>
        </div>
        <p class="no4w-hint">Well-behaved dogs welcome. We'll confirm any details by email.</p>
      </div>

      <div data-role="min-nights-warning"></div>

      <div data-role="price-box"></div>

      <p class="no4w-hint" style="margin-top:10px;">
        Max ${maxAdults} adults, ${maxOcc} total guests.
      </p>
    `;

    this.renderCalendarGrid();
    this.renderChildAges();
    this.renderPriceBox();
    this.renderMinNightsWarning();
    this.bindEvents();
    this.emitValidity();
  }

  private renderSelectedRange(): string {
    const ci = this.state.checkIn;
    const co = this.state.checkOut;
    if (!ci && !co) {
      return '<span>Select your check-in and check-out dates.</span>';
    }
    const nightsHtml =
      ci && co
        ? `<span><strong>${pluralise(diffDays(ci, co), 'night')}</strong></span>`
        : '';
    return `
      <span>Check-in: <strong>${ci ? escapeHTML(formatHumanDate(ci)) : '—'}</strong></span>
      <span>Check-out: <strong>${co ? escapeHTML(formatHumanDate(co)) : '—'}</strong></span>
      ${nightsHtml}
    `;
  }

  private renderCalendarGrid(): void {
    const host = this.host.querySelector<HTMLElement>('[data-role="cal-grid"]');
    if (!host) return;
    const months = [this.leftMonth, addMonths(this.leftMonth, 1)];
    const minMonth = startOfDay(new Date(today().getFullYear(), today().getMonth(), 1));
    const canGoBack = isAfter(this.leftMonth, minMonth);

    host.innerHTML = months
      .map((m, idx) => this.renderMonth(m, idx === 0, canGoBack))
      .join('');

    host.querySelectorAll<HTMLButtonElement>('.no4w-cal-nav').forEach((btn) => {
      btn.addEventListener('click', () => {
        const dir = btn.dataset.dir === 'prev' ? -1 : 1;
        const next = addMonths(this.leftMonth, dir);
        if (dir < 0 && !isAfter(next, addMonths(minMonth, -1))) return;
        this.leftMonth = next;
        this.renderCalendarGrid();
      });
    });

    host.querySelectorAll<HTMLButtonElement>('.no4w-cal-day').forEach((cell) => {
      const iso = cell.dataset.date;
      if (!iso) return;
      cell.addEventListener('click', () => {
        const parsed = fromISODate(iso);
        if (!parsed) return;
        this.onDayClick(parsed);
      });
      cell.addEventListener('mouseenter', () => {
        if (this.state.checkIn && !this.state.checkOut) {
          const parsed = fromISODate(iso);
          if (!parsed) return;
          this.hoverDate = parsed;
          this.paintTentativeRange();
        }
      });
      cell.addEventListener('mouseleave', () => {
        if (this.hoverDate) {
          this.hoverDate = null;
          this.paintTentativeRange();
        }
      });
    });
  }

  private renderMonth(monthAnchor: Date, isLeft: boolean, canGoBack: boolean): string {
    const cells = buildMonthMatrix(monthAnchor);
    const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const navLeft = isLeft
      ? `<button class="no4w-cal-nav" data-dir="prev" ${
          canGoBack ? '' : 'disabled'
        } aria-label="Previous month">‹</button>`
      : '<span style="width:30px;"></span>';
    const navRight = !isLeft
      ? `<button class="no4w-cal-nav" data-dir="next" aria-label="Next month">›</button>`
      : '<span style="width:30px;"></span>';

    return `
      <div class="no4w-cal-month">
        <div class="no4w-cal-month-header">
          ${navLeft}
          <div class="no4w-cal-month-label">${escapeHTML(formatMonthLabel(monthAnchor))}</div>
          ${navRight}
        </div>
        <div class="no4w-cal-weekdays">
          ${weekdays
            .map((w) => `<div class="no4w-cal-weekday">${w}</div>`)
            .join('')}
        </div>
        <div class="no4w-cal-grid">
          ${cells.map((d) => this.renderDay(d, monthAnchor)).join('')}
        </div>
      </div>
    `;
  }

  private renderDay(d: Date, monthAnchor: Date): string {
    const inMonth = isSameMonth(d, monthAnchor);
    const isPast = isBefore(d, today());
    const blocked = this.isDateBlocked(d);
    const { isStart, isEnd, isInRange } = this.rangeFlags(d);

    const classes = ['no4w-cal-day'];
    if (!inMonth) classes.push('is-other-month');
    if (isPast) classes.push('is-past');
    if (blocked && inMonth) classes.push('is-blocked');
    if (isStart) classes.push('is-range-start');
    if (isEnd) classes.push('is-range-end');
    if (isInRange) classes.push('is-range');

    const disabled = !inMonth || isPast || blocked;
    return `
      <button
        type="button"
        class="${classes.join(' ')}"
        data-date="${toISODate(d)}"
        ${disabled ? 'disabled' : ''}
        aria-label="${escapeHTML(formatHumanDate(d))}"
      >${d.getDate()}</button>
    `;
  }

  private rangeFlags(d: Date): {
    isStart: boolean;
    isEnd: boolean;
    isInRange: boolean;
  } {
    const { checkIn, checkOut } = this.state;
    const isStart = !!checkIn && isSameDay(d, checkIn);
    const isEnd = !!checkOut && isSameDay(d, checkOut);
    const isInRange =
      !!checkIn &&
      !!checkOut &&
      isAfter(d, checkIn) &&
      isBefore(d, checkOut);
    return { isStart, isEnd, isInRange };
  }

  private paintTentativeRange(): void {
    if (!this.state.checkIn) return;
    const grid = this.host.querySelector<HTMLElement>('[data-role="cal-grid"]');
    if (!grid) return;
    const cells = grid.querySelectorAll<HTMLButtonElement>('.no4w-cal-day');
    cells.forEach((cell) => {
      cell.classList.remove('is-tentative-hover');
    });
    if (!this.hoverDate || this.state.checkOut) return;
    if (!isAfter(this.hoverDate, this.state.checkIn)) return;
    cells.forEach((cell) => {
      const iso = cell.dataset.date;
      if (!iso) return;
      const d = fromISODate(iso);
      if (!d) return;
      if (this.state.checkIn && this.hoverDate && isAfter(d, this.state.checkIn) && isBefore(d, this.hoverDate)) {
        cell.classList.add('is-tentative-hover');
      }
    });
  }

  private renderChildAges(): void {
    const host = this.host.querySelector<HTMLElement>('[data-role="child-ages"]');
    if (!host) return;
    if (this.state.numChildren <= 0) {
      host.innerHTML = '';
      return;
    }
    const rows: string[] = [];
    for (let i = 0; i < this.state.numChildren; i++) {
      const v = this.state.childAges[i] ?? '';
      rows.push(`
        <div class="no4w-field">
          <label for="no4w-child-${i}">Child ${i + 1} — age or age range</label>
          <input
            id="no4w-child-${i}"
            type="text"
            data-child-index="${i}"
            value="${escapeHTML(v)}"
            placeholder="e.g. 5, or 2–4"
            autocomplete="off"
          />
        </div>
      `);
    }
    host.innerHTML = rows.join('');
    host.querySelectorAll<HTMLInputElement>('input[data-child-index]').forEach((el) => {
      el.addEventListener('input', () => {
        const i = Number(el.dataset.childIndex);
        if (!Number.isFinite(i)) return;
        const next = [...this.state.childAges];
        while (next.length <= i) next.push('');
        next[i] = el.value;
        this.state.childAges = next;
        this.opts.onStateChange(this.getState());
        this.emitValidity();
      });
    });
  }

  private renderPriceBox(): void {
    const host = this.host.querySelector<HTMLElement>('[data-role="price-box"]');
    if (!host) return;
    if (this.opts.flow !== 'booking') {
      host.innerHTML = '';
      return;
    }
    if (!this.state.checkIn || !this.state.checkOut) {
      host.innerHTML = '';
      return;
    }
    if (!this.meetsMinNights()) {
      host.innerHTML = '';
      return;
    }

    if (this.priceLoading) {
      host.innerHTML = `
        <div class="no4w-price">
          <h4>Price</h4>
          <p class="no4w-price-loading">
            <span class="no4w-loader no4w-loader--dark"></span> Calculating…
          </p>
        </div>`;
      return;
    }
    if (this.priceError) {
      host.innerHTML = `
        <div class="no4w-banner no4w-banner--error">${escapeHTML(this.priceError)}</div>`;
      return;
    }

    const p = this.state.serverPrice ?? this.localEstimate();
    if (!p) {
      host.innerHTML = '';
      return;
    }
    host.innerHTML = `
      <div class="no4w-price">
        <h4>Price</h4>
        <div class="no4w-price-row">
          <span>Nightly rate</span>
          <span>${formatPence(p.nightlyRatePence)}</span>
        </div>
        <div class="no4w-price-row">
          <span>${pluralise(p.numNights, 'night')}</span>
          <span>${formatPence(p.nightlyRatePence * p.numNights)}</span>
        </div>
        <div class="no4w-price-row is-total">
          <span>Total</span>
          <span>${formatPence(p.totalPricePence)}</span>
        </div>
        <div class="no4w-price-row is-deposit">
          <span>Deposit payable today</span>
          <span>${formatPence(p.depositPence)}</span>
        </div>
        <div class="no4w-price-row is-balance">
          <span>Balance${
            (() => {
              if (!('balanceDueDate' in p) || !p.balanceDueDate) return '';
              const parsed = fromISODate(p.balanceDueDate);
              return parsed ? ' due by ' + escapeHTML(formatHumanDate(parsed)) : '';
            })()
          }</span>
          <span>${formatPence(p.balancePence)}</span>
        </div>
      </div>
    `;
  }

  private renderMinNightsWarning(): void {
    const host = this.host.querySelector<HTMLElement>(
      '[data-role="min-nights-warning"]',
    );
    if (!host) return;
    if (!this.state.checkIn || !this.state.checkOut) {
      host.innerHTML = '';
      return;
    }
    if (this.meetsMinNights()) {
      host.innerHTML = '';
      return;
    }
    const need = this.requiredMinNights();
    const have = diffDays(this.state.checkIn, this.state.checkOut);
    host.innerHTML = `
      <div class="no4w-banner no4w-banner--info">
        Minimum stay is ${pluralise(need, 'night')}${
          this.isSummerStay(this.state.checkIn)
            ? ' during the summer school holidays'
            : ''
        }. You've selected ${pluralise(have, 'night')}.
      </div>`;
  }

  private localEstimate(): PriceBreakdown | null {
    if (!this.state.checkIn || !this.state.checkOut || !this.availability) return null;
    const nights = diffDays(this.state.checkIn, this.state.checkOut);
    if (nights <= 0) return null;
    const totalGuests = this.state.numAdults + this.state.numChildren;
    const est = estimateBreakdown({
      numNights: nights,
      totalGuests,
      baseRatePence: this.availability.baseRatePence,
      couplesDiscountPct: this.availability.couplesDiscountPct,
      perPersonReductionPct: this.availability.perPersonReductionPct,
      depositPct: this.availability.depositPct,
    });
    const balanceDue = addDays(this.state.checkIn, -56); // approx 8 weeks before; server overrides
    return {
      numNights: nights,
      nightlyRatePence: est.nightlyRatePence,
      totalPricePence: est.totalPricePence,
      depositPence: est.depositPence,
      balancePence: est.balancePence,
      balanceDueDate: toISODate(balanceDue),
      discountType:
        totalGuests <= 2 ? 'couples' : totalGuests >= 4 ? 'taper' : 'none',
      discountPct: 0,
    };
  }

  /* ----------------------------------------------------------------- */
  /*  Events                                                            */
  /* ----------------------------------------------------------------- */

  private bindEvents(): void {
    // Steppers
    this.host
      .querySelectorAll<HTMLElement>('[data-stepper]')
      .forEach((stepper) => {
        const which = stepper.dataset.stepper as 'adults' | 'children';
        stepper.querySelectorAll<HTMLButtonElement>('button').forEach((btn) => {
          btn.addEventListener('click', () => {
            const delta = Number(btn.dataset.step);
            this.applyStep(which, delta);
          });
        });
      });

    // Pets toggle
    this.host
      .querySelectorAll<HTMLButtonElement>('[data-pets]')
      .forEach((btn) => {
        btn.addEventListener('click', () => {
          const v = btn.dataset.pets === 'yes';
          if (v === this.state.hasPets) return;
          this.state.hasPets = v;
          this.opts.onStateChange(this.getState());
          this.host
            .querySelectorAll<HTMLButtonElement>('[data-pets]')
            .forEach((b) =>
              b.classList.toggle(
                'is-active',
                (b.dataset.pets === 'yes') === this.state.hasPets,
              ),
            );
          this.emitValidity();
        });
      });
  }

  private applyStep(which: 'adults' | 'children', delta: number): void {
    if (!this.availability) return;
    const maxAdults = this.availability.maxAdults || MAX_ADULTS_FALLBACK;
    const maxOcc = this.availability.maxOccupancy || MAX_OCC_FALLBACK;
    if (which === 'adults') {
      const next = this.state.numAdults + delta;
      if (next < 1) return;
      if (next > maxAdults) return;
      if (next + this.state.numChildren > maxOcc) return;
      this.state.numAdults = next;
    } else {
      const next = this.state.numChildren + delta;
      if (next < 0) return;
      if (this.state.numAdults + next > maxOcc) return;
      this.state.numChildren = next;
      // resize childAges
      if (next < this.state.childAges.length) {
        this.state.childAges = this.state.childAges.slice(0, next);
      }
    }
    // Update displayed value without full re-render
    const stepperEl = this.host.querySelector<HTMLElement>(
      `[data-stepper="${which}"] [data-value]`,
    );
    if (stepperEl) {
      stepperEl.textContent = String(
        which === 'adults' ? this.state.numAdults : this.state.numChildren,
      );
    }
    if (which === 'children') {
      this.renderChildAges();
    }
    this.opts.onStateChange(this.getState());
    this.emitValidity();
    this.maybeFetchPrice();
  }

  private onDayClick(d: Date): void {
    if (isBefore(d, today())) return;
    if (this.isDateBlocked(d)) return;

    const { checkIn, checkOut } = this.state;

    if (!checkIn || (checkIn && checkOut)) {
      // start a fresh selection
      this.state.checkIn = d;
      this.state.checkOut = null;
    } else if (checkIn && !checkOut) {
      if (isBefore(d, checkIn) || isSameDay(d, checkIn)) {
        // clicking before or on start resets start
        this.state.checkIn = d;
        this.state.checkOut = null;
      } else {
        // ensure no blocked day lives between checkIn and d
        if (this.rangeContainsBlocked(checkIn, d)) {
          // can't select across a blocked night — restart selection at d
          this.state.checkIn = d;
          this.state.checkOut = null;
        } else {
          this.state.checkOut = d;
        }
      }
    }

    this.refreshSelectionUI();
    this.opts.onStateChange(this.getState());
    this.emitValidity();
    this.maybeFetchPrice();
  }

  private refreshSelectionUI(): void {
    const range = this.host.querySelector<HTMLElement>(
      '[data-role="selected-range"]',
    );
    if (range) range.innerHTML = this.renderSelectedRange();
    this.renderCalendarGrid();
    this.renderMinNightsWarning();
    this.renderPriceBox();
  }

  /* ----------------------------------------------------------------- */
  /*  Availability helpers                                              */
  /* ----------------------------------------------------------------- */

  private isDateBlocked(d: Date): boolean {
    if (!this.availability) return false;
    for (const r of this.availability.blockedRanges) {
      const bStart = fromISODate(r.start);
      const bEnd = fromISODate(r.end);
      if (!bStart || !bEnd) continue;
      if (isWithin(d, bStart, bEnd)) return true;
    }
    return false;
  }

  private rangeContainsBlocked(startInclusive: Date, endExclusive: Date): boolean {
    if (!this.availability) return false;
    const ranges: BlockedRange[] = this.availability.blockedRanges;
    for (const r of ranges) {
      const bStart = fromISODate(r.start);
      const bEnd = fromISODate(r.end);
      if (!bStart || !bEnd) continue;
      if (rangeIntersectsBlocked(startInclusive, endExclusive, bStart, bEnd)) {
        return true;
      }
    }
    return false;
  }
}
